import {
  escribirCantidad,
  type CantidadFacturable,
  type ItemDelTrabajo,
  type RepartoDelMes,
} from './reparto';

/**
 * El mes visto por cliente, que es como se factura.
 *
 * ## El problema que resuelve
 *
 * El resumen del mes está ordenado por fecha, porque así se carga y así se
 * revisa contra la planilla. Pero facturar no se hace por fecha: se hace por
 * cliente. Un cliente con cuatro trabajos en septiembre los tiene desparramados
 * entre los otros dieciocho, y armar su factura obligaba a ir juntándolos a
 * ojo, anotando cantidades en un papel y sumando a mano.
 *
 * Acá están juntos: los cuatro trabajos, las cantidades SUMADAS de los cuatro,
 * y el total. Es lo que se escribe en la factura, tal como se escribe.
 *
 * ## Por qué las cantidades se suman y los importes no del todo
 *
 * Las cantidades se suman siempre: ochenta tactos más cien tactos son ciento
 * ochenta tactos, y eso es un renglón de la factura.
 *
 * Los importes no: un trabajo sin precio de novillo todavía no vale nada
 * —`NULL` es PENDIENTE, nunca 0— así que sumarlo como cero daría un total que
 * parece completo y no lo está. Se suma lo que tiene precio y se dice cuántos
 * faltan, que es la única forma honesta de mostrar un total a medias.
 */

/** Un trabajo de ese cliente, con lo poco que hace falta para reconocerlo. */
export interface TrabajoDelCliente {
  work_session_id: string;
  work_date: string;
  establishment_name: string | null;
  total_cents: number | null;
  is_billed: boolean;
  is_paid: boolean;
  /** A quién se le imputa la facturación de ESTE trabajo. */
  billed_to_participant_id: string | null;
}

export interface ClienteDelMes {
  client_name: string;
  /** Sus trabajos del mes, del más viejo al más nuevo. */
  trabajos: TrabajoDelCliente[];
  /** Las cantidades de todos ellos, sumadas: los renglones de la factura. */
  cantidades: CantidadFacturable[];
  /** La suma de lo que YA tiene precio. */
  total_cents: number;
  /** Cuántos trabajos todavía no lo tienen. Con uno solo, el total es parcial. */
  sinPrecio: number;
  /** Los que faltan facturar. */
  porFacturar: string[];
  /** Los que ya se facturaron y falta cobrar. */
  porCobrar: string[];
  /**
   * Quién factura a este cliente, cuando es una sola persona en todo el mes.
   *
   * Es lo que se pinta: el nombre del cliente con el color de quien lo factura.
   * `null` cuando el mes está repartido entre varios, o cuando el único que hay
   * es «sin asignar» —que no tiene color ni debe parecer que lo tiene—.
   */
  facturadoPor: string | null;
  /**
   * Los trabajos del mes de este cliente NO los factura todo la misma persona.
   *
   * Pasa de verdad: a un mismo cliente se le factura una cosa por uno y otra
   * por otro. Entonces no hay un color para el cliente, y decir «factura
   * Manuel» sería falso. Se dice que son varios y cada trabajo muestra el suyo.
   */
  facturanVarios: boolean;
}

/** Lo que se va acumulando de un honorario mientras se recorren los trabajos. */
interface Acumulado {
  nombre: string;
  cantidad: number;
  /** En cuántos trabajos apareció. Es la cantidad de los que se cobran por trabajo. */
  apariciones: number;
  /** Apareció al menos una vez cobrado por unidad. */
  porUnidad: boolean;
  /** Alguna de las cantidades sumadas no es confiable (Spec §50). */
  dudosa: boolean;
}

function acumular(items: readonly ItemDelTrabajo[], en: Map<string, Acumulado>): void {
  for (const i of items) {
    const a = en.get(i.name) ?? {
      nombre: i.name,
      cantidad: 0,
      apariciones: 0,
      porUnidad: false,
      dudosa: false,
    };
    a.apariciones += 1;
    if (i.unreliable) a.dudosa = true;
    if (!i.per_job) {
      a.porUnidad = true;
      a.cantidad += i.quantity;
    }
    en.set(i.name, a);
  }
}

/**
 * Cómo se escribe cada honorario acumulado.
 *
 * Tres casos, y los tres importan:
 *
 *  * **Por unidad**: la suma. Es el renglón de la factura.
 *  * **Por trabajo**: no tiene unidades que sumar, pero se cobra una vez por
 *    visita, así que lo que se factura es EN CUÁNTAS VISITAS apareció. Con una
 *    sola no se escribe el «1», que no agrega nada.
 *  * **Dudosa**: alcanza con que una de las cantidades sumadas sea de las que
 *    la migración marcó como manipuladas (Spec §50) para que la suma no se
 *    pueda afirmar. Se dice, en vez de mostrar un número que parece bueno.
 *
 * Si el mismo honorario aparece de las dos formas —porque el tarifario cambió
 * en el medio— manda «por unidad»: las unidades existen y hay que facturarlas.
 */
function escribir(a: Acumulado): CantidadFacturable {
  if (a.dudosa) return { nombre: a.nombre, cantidad: null, dudosa: true };
  if (a.porUnidad) return { nombre: a.nombre, cantidad: escribirCantidad(a.cantidad) };
  return {
    nombre: a.nombre,
    cantidad: a.apariciones > 1 ? escribirCantidad(a.apariciones) : null,
  };
}

/**
 * Los clientes del mes, con todo lo suyo junto.
 *
 * Se agrupa por NOMBRE porque es lo que trae el reparto, y es el mismo criterio
 * con el que ya se arma la hoja de Excel que se usa para repartir la plata.
 *
 * El orden es alfabético y no «los que faltan primero», a propósito: facturar
 * un cliente lo sacaría de arriba y haría saltar la lista bajo el dedo justo
 * después de tocarla. Cuántos faltan se dice con un número; dónde está cada uno
 * no cambia.
 */
export function clientesDelMes(d: RepartoDelMes): ClienteDelMes[] {
  const porNombre = new Map<string, { cliente: ClienteDelMes; items: Map<string, Acumulado> }>();
  const kmPorCliente = new Map<string, number>();

  for (const w of d.works) {
    const entrada = porNombre.get(w.client_name) ?? {
      cliente: {
        client_name: w.client_name,
        trabajos: [],
        cantidades: [],
        total_cents: 0,
        sinPrecio: 0,
        porFacturar: [],
        porCobrar: [],
        facturadoPor: null,
        facturanVarios: false,
      },
      items: new Map<string, Acumulado>(),
    };
    const c = entrada.cliente;

    c.trabajos.push({
      work_session_id: w.work_session_id,
      work_date: w.work_date,
      establishment_name: w.establishment_name,
      total_cents: w.total_cents,
      is_billed: w.is_billed,
      is_paid: w.is_paid,
      billed_to_participant_id: w.billed_to_participant_id,
    });

    if (w.total_cents === null) c.sinPrecio += 1;
    else c.total_cents += w.total_cents;

    if (!w.is_billed) c.porFacturar.push(w.work_session_id);
    else if (!w.is_paid) c.porCobrar.push(w.work_session_id);

    acumular(w.items ?? [], entrada.items);
    if (w.mobility_km != null && w.mobility_km > 0) {
      kmPorCliente.set(w.client_name, (kmPorCliente.get(w.client_name) ?? 0) + w.mobility_km);
    }

    porNombre.set(w.client_name, entrada);
  }

  return [...porNombre.values()]
    .map(({ cliente, items }) => {
      const cantidades = [...items.values()].map(escribir);
      const km = kmPorCliente.get(cliente.client_name);
      if (km) cantidades.push({ nombre: 'Movilidad', cantidad: `${escribirCantidad(km)} km` });
      cliente.cantidades = cantidades;
      cliente.trabajos.sort((a, b) => a.work_date.localeCompare(b.work_date));

      // Quién factura. Con uno solo se puede pintar el cliente con su color;
      // con varios no hay un color que no mienta.
      const facturadores = new Set(cliente.trabajos.map((t) => t.billed_to_participant_id));
      cliente.facturanVarios = facturadores.size > 1;
      cliente.facturadoPor = facturadores.size === 1 ? ([...facturadores][0] ?? null) : null;

      return cliente;
    })
    .sort((a, b) => a.client_name.localeCompare(b.client_name, 'es'));
}

/** Cuántos trabajos del mes todavía no se facturaron. */
export const faltanFacturar = (cs: readonly ClienteDelMes[]): number =>
  cs.reduce((n, c) => n + c.porFacturar.length, 0);
